import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { getAgentSkills, type AgentSkill } from "@/apis/agent"

/**
 * 内置技能目录（兜底）：参考小云雀等业界创作 Agent 的技能体系调研整理，
 * 适配 HiWaifu 的角色/二次元内容场景。
 * PHP `/client/agent/skills` 可下发覆盖（运营可增删），接口失败时使用本目录。
 * 名称/描述展示时优先取前端 i18n（skills.<id>.name / .desc），保证多语言。
 */
export const BUILT_IN_SKILLS: AgentSkill[] = [
  {
    id: "story-short",
    icon: "🎬",
    name: "Story Short Film",
    description: "Turn one idea into a complete narrative short with script, shots and footage.",
    target: "drama",
    featured: true,
  },
  {
    id: "storyboard",
    icon: "🎞️",
    name: "Storyboard Writer",
    description: "Expand a theme or reference image into a shot-by-shot storyboard.",
    target: "agent",
    featured: true,
  },
  {
    id: "drama-series",
    icon: "📺",
    name: "Mini Drama",
    description: "Parse or write a script, build characters and scenes, then generate episodes.",
    target: "drama",
    featured: true,
  },
  {
    id: "character-card",
    icon: "🪪",
    name: "Character Card",
    description: "Design an original character and publish it as a chattable character card.",
    target: "agent",
    featured: true,
  },
  {
    id: "ppt-deck",
    icon: "📊",
    name: "PPT Deck",
    description: "Turn a topic or document into a native, editable PowerPoint deck.",
    target: "agent",
    featured: true,
  },
  {
    id: "character-sheet",
    icon: "🧍",
    name: "Character Turnaround",
    description: "One-click front / side / back reference sheet for your character.",
    target: "image",
    prompt_template:
      "Character turnaround sheet, front view, side view and back view of the same character, full body, consistent design and outfit, clean neutral background, model sheet style",
    featured: true,
  },
  {
    id: "anime-mv",
    icon: "🎵",
    name: "Anime MV",
    description: "Generate an atmospheric anime music-video style clip from a mood or song theme.",
    target: "video",
    prompt_template:
      "Anime music video scene, cinematic lighting, emotional atmosphere, dynamic camera movement, high detail illustration style",
  },
  {
    id: "viral-remake",
    icon: "🔥",
    name: "Viral Remake",
    description: "Break down a trending video's hook and recreate it with your character.",
    target: "agent",
  },
  {
    id: "oc-card",
    icon: "🪪",
    name: "OC Profile Card",
    description: "Design an original-character card with portrait, palette and personality.",
    target: "image",
    prompt_template:
      "Original character profile card, waist-up portrait, name plate and personality keywords layout, color palette swatches, clean graphic design background",
  },
  {
    id: "wallpaper",
    icon: "🖼️",
    name: "HD Wallpaper",
    description: "Cinematic wallpaper of your character for phone or desktop.",
    target: "image",
    prompt_template:
      "Ultra detailed cinematic wallpaper, dramatic lighting, depth of field, stunning composition, 8k quality",
  },
  {
    id: "sticker-pack",
    icon: "😝",
    name: "Sticker Pack",
    description: "A 3x3 grid of chibi emotion stickers of the same character.",
    target: "image",
    prompt_template:
      "3x3 sticker sheet of the same chibi character, nine different emotions: happy, angry, crying, laughing, shy, surprised, sleepy, love, thumbs up, white background, die-cut sticker style",
  },
  {
    id: "dance-loop",
    icon: "💃",
    name: "Dance Video",
    description: "Make your character dance with smooth, natural motion.",
    target: "video",
    prompt_template:
      "Character dancing to an upbeat rhythm, smooth natural body motion, dynamic camera, stage lighting, loopable clip",
  },
  {
    id: "seamless-transition",
    icon: "🌀",
    name: "Seamless Transition",
    description: "Multiple images become one continuous shot with seamless transitions.",
    target: "video",
    prompt_template:
      "One continuous shot, seamless morph transitions between scenes, smooth camera flow, cinematic pacing",
  },
  {
    id: "story-ad",
    icon: "🛍️",
    name: "Story Ad",
    description: "Weave a product into a high-tension mini plot that sells.",
    target: "agent",
  },
  {
    id: "prompt-extract",
    icon: "🔍",
    name: "Prompt Extraction",
    description: "Reverse-engineer the prompt from any image you upload.",
    target: "agent",
  },
  {
    id: "knowledge-video",
    icon: "📚",
    name: "Knowledge Video",
    description: "Turn narration text into an illustrated explainer video plan.",
    target: "agent",
  },
  {
    id: "robot-search",
    icon: "🔎",
    name: "Robot Search",
    description: "Find platform characters by text or image, then reuse their avatar/persona as creative assets.",
    target: "agent",
  },
]

/** 技能目录：后端数据库下发（官方 + 我的），失败回退内置目录 */
export function useSkills() {
  const { data, isLoading } = useQuery({
    queryKey: ["agent", "skills"],
    queryFn: () => getAgentSkills(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  const { skills, official, mine, recommended, recommendTitle } = useMemo(() => {
    const payload = data?.status === 200 ? data.data : null
    const merged = (payload?.list?.length ? payload.list : BUILT_IN_SKILLS) as AgentSkill[]
    // 空白对话推荐位：后端可下发 recommended（技能清单）与 recommend_title（推荐文案）；
    // 未下发时回退 featured 技能
    const recommendedList = (
      payload?.recommended?.length ? payload.recommended : merged.filter((s) => s.featured)
    ).slice(0, 8) as AgentSkill[]
    return {
      skills: merged,
      official: (payload?.official?.length ? payload.official : merged) as AgentSkill[],
      mine: (payload?.mine ?? []) as AgentSkill[],
      recommended: recommendedList,
      recommendTitle: payload?.recommend_title ?? "",
    }
  }, [data])

  return { skills, official, mine, recommended, recommendTitle, isLoading }
}
